import type { ApiError } from '@/lib/axios';
import { FLEET_ERROR_CODES, type FleetErrorBody, type FleetErrorCode } from '@/api/fleet/types';

/**
 * What went wrong with a fleet call, as one word the screens switch on.
 *
 * echo answers every expected failure with `{ error: <code> }` (Contract C). Two more cases have
 * no code: echo itself unreachable (`network`) and anything else (`unknown`). axios.ts normalises
 * errors so the response body sits on `error.data`, never on `error.response`.
 */
export type FleetFailure = FleetErrorCode | 'network' | 'unknown';

function isApiError(error: unknown): error is ApiError {
  return error instanceof Error && 'isNetworkError' in error;
}

function isFleetErrorCode(value: unknown): value is FleetErrorCode {
  return typeof value === 'string' && (FLEET_ERROR_CODES as readonly string[]).includes(value);
}

export function fleetFailure(error: unknown): FleetFailure | null {
  if (error == null) return null;
  if (isApiError(error)) {
    const body = error.data as FleetErrorBody | undefined;
    if (isFleetErrorCode(body?.error)) return body.error;
    if (error.isNetworkError) return 'network';
  }
  return 'unknown';
}

/** True when echo answered with one of its own codes: a definite answer, not worth retrying. */
export function hasFleetCode(error: unknown): boolean {
  if (!isApiError(error)) return false;
  return isFleetErrorCode((error.data as FleetErrorBody | undefined)?.error);
}

/** The i18n key of a failure's sentence: `fleet.errors.<code>`. */
export const fleetErrorKey = (failure: FleetFailure): string => `fleet.errors.${failure}`;

/** A source that is not set up at all: its screen explains how to connect it instead of an error. */
export type NotConnectedSource = 'traccar' | 'milo' | 'once';

export function notConnectedSource(failure: FleetFailure | null): NotConnectedSource | null {
  if (failure === 'traccar_not_connected') return 'traccar';
  if (failure === 'milo_not_connected') return 'milo';
  if (failure === 'once_not_connected') return 'once';
  return null;
}
